import React from 'react'

import { Cockpit, Icon, IconColor, IconName, MacronutrientCard } from 'Components'
import { buildCdnImageUrl } from 'Config'
import { Nutritions } from 'Models'
import PlanSectionTitle from 'Pages/JournalPage/components/PlanSectionTitle/PlanSectionTitle'
import { Formatter } from 'Utils'

const demoAcceptedNutritions: Nutritions = {
  energy: 0,
  carbohydrates: 50,
  protein: 30,
  fat: 10
}

const demoGoalNutritions: Nutritions = {
  energy: 0,
  carbohydrates: 100,
  protein: 100,
  fat: 100
}

export const TourWrapper: React.FC<{ markerStyle?: React.CSSProperties; children: React.ReactNode }> = ({
  markerStyle,
  children
}) => (
  <div className="tour-wrapper">
    {markerStyle && <div className="tour-marker" style={markerStyle} />}
    {children}
  </div>
)

export const JournalHeaderDemo: React.FC = () => (
  <div className="journal-header mb-3">
    <div className="journal-header-actions journal-header-actions-start">
      <span title="Rezepte" className="journal-header-action">
        <Icon name={IconName.book} color={IconColor.GRAY_1} />
      </span>
      <span title="Mein Essen" className="journal-header-action">
        <Icon name={IconName.utensils} color={IconColor.GRAY_1} />
      </span>
      <span title="Favoriten" className="journal-header-action">
        <Icon name={IconName.heart} color={IconColor.GRAY_1} />
      </span>
    </div>
    <div className="datepicker2-nav-wrapper">
      <div className="datepicker2-nav">
        <span className="d-flex iconbutton">
          <Icon name={IconName.arrowleft} color={IconColor.GRAY_1} />
        </span>
        <div className="datepicker2-nav-text">
          {Formatter.formatDate(new Date(), '[Heute], D. MMM YYYY', 'D. MMM YYYY')}
        </div>
        <span className="d-flex iconbutton">
          <Icon name={IconName.arrowright} color={IconColor.GRAY_1} />
        </span>
      </div>
    </div>
    <div className="journal-header-actions journal-header-actions-end">
      <span title="Nährwertanalyse" className="journal-header-action">
        <Icon name={IconName.piechart} color={IconColor.GRAY_1} />
      </span>
      <span title="Einstellungen" className="journal-header-action">
        <Icon name={IconName.cog} color={IconColor.GRAY_1} />
      </span>
    </div>
  </div>
)

export const CockpitDemo: React.FC = () => (
  <Cockpit isLoading={false} acceptedDirectEnergyIntake={1449} goalEnergy={1434} acceptedEnergyMotionSum={888} />
)

export const JournalActionsDemo: React.FC = () => (
  <div className="journal-actions-container">
    <span className="journal-action">
      <span className="journal-action-icon" aria-hidden="true" />
      <span>Zum Tagebuch hinzufügen</span>
    </span>
    <span title="Körpermasse eintragen" className="journal-action">
      <Icon name={IconName.scale} color={IconColor.BLUE_2} />
      <span>Körpermasse eintragen</span>
    </span>
  </div>
)

export const JournalCardDemo: React.FC = () => (
  <div className="standardcard is-eatable">
    <div className="standardcard-image cursor">
      <img src={buildCdnImageUrl('demo/lunch-4.jpg')} alt="Poulet-Reis-Bowl" />
    </div>
    <div className="standardcard-content cursor">
      <span className="standardcard-title">Poulet-Reis-Bowl</span>
      <span className="standardcard-source">eBalance Rezept</span>
      <span>320 kcal / 1 Portion (250 g)</span>
    </div>
    <div className="standardcard-icons">
      <span className="d-flex iconbutton">
        <Icon name={IconName.menuv} color={IconColor.GRAY_2} />
      </span>
      <ul className="standardcard-dropdown">
        <li>
          <span className="d-flex iconbutton">
            <div className="icon icon-edit-gray-1" />
            <span className="ml-1">Editieren</span>
          </span>
        </li>
        <li>
          <span className="d-flex iconbutton">
            <div className="icon icon-copy-gray-1" />
            <span className="ml-1">Kopieren</span>
          </span>
        </li>
        <li>
          <span className="d-flex iconbutton">
            <div className="icon icon-move-gray-1" />
            <span className="ml-1">Verschieben</span>
          </span>
        </li>
        <li>
          <span className="d-flex iconbutton">
            <div className="icon icon-heart-gray-1" />
            <span className="ml-1">Favorisieren</span>
          </span>
        </li>
        <li>
          <span className="d-flex iconbutton">
            <div className="icon icon-trash-gray-1" />
            <span className="ml-1">Löschen</span>
          </span>
        </li>
      </ul>
    </div>
  </div>
)

export const MenuPlanCardDemo: React.FC = () => (
  <div className="col-auto basiccard px-2 pb-2 mb-2">
    <PlanSectionTitle title="Menüplan" variant="menu" />
    <div className="standardcard is-eatable is-menuplan">
      <div className="standardcard-image">
        <img src={buildCdnImageUrl('demo/lunch-1.jpg')} alt="Birchermüesli mit Beeren" />
      </div>
      <div className="standardcard-content cursor">
        <span className="standardcard-title">Birchermüesli mit Beeren</span>
        <span className="standardcard-source">eBalance Rezept</span>
        <span>1 Portion, 320 kcal</span>
      </div>
      <div className="standardcard-icons">
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'center' }}>
          <span className="d-flex iconbutton">
            <Icon name={IconName.plus} color={IconColor.GRAY_2} />
          </span>
        </div>
        <span className="d-flex iconbutton">
          <Icon name={IconName.menuv} color={IconColor.GRAY_2} />
        </span>
      </div>
    </div>
  </div>
)

export const SidebarWidgetsDemo: React.FC = () => (
  <MacronutrientCard
    isLoading={false}
    acceptedNutritions={demoAcceptedNutritions}
    goalNutritions={demoGoalNutritions}
  />
)

export const SettingsSwitchDemo: React.FC = () => (
  <div className="basiccard px-3 pt-5 pb-3 mb-2">
    <div className="row justify-content-between">
      <span className="mt-2">Menüplan im Tagebuch anzeigen</span>
      <span className="mt-2">
        <label className="toggle">
          <input type="checkbox" defaultChecked readOnly />
          <span className="label-left">Nein</span>
          <span className="label-right">Ja</span>
        </label>
      </span>
    </div>
  </div>
)
